import axios from 'axios';

import ICoreApiProvider from './ICoreApiProvider';
import IGbContext from '../../interfaces/IGbContext';
import ICoreRoom from '../../interfaces/ICoreRoom';
import { ConfigurationState } from '../../store/configurationState/interfaces';
import handleApiError from '../bimVisualizerApi/errorHandler';
import { normalizeGuid } from '../../utils/guidUtils';
import { RoomNoteSaveResult, roomNoteSaveErrorFromApiError } from './RoomNoteSaveResult';
import { RoomNoteSaveErrorEnum } from '../../enums/RoomNoteSaveErrorEnum';
import { coreApartmentRoomsSchema } from './schema/coreApartmentRoomsSchema';
import { coreUpdateRoomsResponseSchema } from './schema/coreUpdateRoomsResponseSchema';
import { coreUserRightsCheckSchema } from './schema/coreUserRightsCheckSchema';

// Treat a null, empty or whitespace-only comment as no note
const normalizeComment = (comment: string | null | undefined): string | null =>
    comment?.trim() ? comment.trim() : null;

export default class CoreApiProvider implements ICoreApiProvider {
    configuration: ConfigurationState;
    token?: string;

    constructor(configuration: ConfigurationState, token?: string) {
        this.configuration = configuration;
        this.token = token;
    }

    // Null when CoreAPI's url isn't configured or there is no token, so callers can skip the request
    private getProjectUrl(gbContext: IGbContext): string | null {
        const coreApiUrl = this.configuration.coreApiUrl;
        if (!this.token || !coreApiUrl) {
            // Without this, room notes and the right to edit them silently disappear
            console.warn(`CoreAPI request skipped: ${!coreApiUrl ? 'VITE_CORE_API_URL is not configured' : 'no token'}`);
            return null;
        }

        const baseUrl = coreApiUrl.href.endsWith('/') ? coreApiUrl.href : `${coreApiUrl.href}/`;
        return `${baseUrl}api/v6/private/organisations/${gbContext.organisationId}/tenants/${gbContext.tenantId}/projects/${gbContext.projectId}`;
    }

    private getHeaders() {
        return {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${this.token}`
        };
    }

    getApartmentRooms = async (gbContext: IGbContext): Promise<ICoreRoom[] | null> => {
        const projectUrl = this.getProjectUrl(gbContext);
        if (!projectUrl || !gbContext.apartmentId) return null;

        try {
            const response = await axios.get(`${projectUrl}/apartments/${gbContext.apartmentId}`, {
                headers: this.getHeaders(),
                params: { rooms: true }
            });

            const rooms = coreApartmentRoomsSchema.parse(response.data).data.rooms ?? [];

            // Rooms without an ifcguid can never match a space
            return rooms.flatMap(room => {
                const globalId = normalizeGuid(room.ifcguid);
                return globalId ? [{ id: room.id, globalId, note: normalizeComment(room.comment) }] : [];
            });
        } catch (error) {
            if (!handleApiError(error)) {
                console.error('Error fetching apartment rooms:', error);
            }
        }
        return null;
    };

    // CoreAPI checks no right and doesn't check that the room belongs to the project, so only pass ids from getApartmentRooms
    updateRoomComment = async (gbContext: IGbContext, roomId: number, comment: string | null): Promise<RoomNoteSaveResult> => {
        const projectUrl = this.getProjectUrl(gbContext);
        if (!projectUrl) return { ok: false, error: RoomNoteSaveErrorEnum.Unauthorized };

        const normalized = normalizeComment(comment);

        try {
            const response = await axios.put(
                `${projectUrl}/rooms/update`,
                { entityIds: [roomId], properties: { comment: normalized } },
                { headers: this.getHeaders() });

            const parsed = coreUpdateRoomsResponseSchema.parse(response.data);
            if (parsed.error) return { ok: false, error: RoomNoteSaveErrorEnum.Failed };
            if (parsed.data === 0) return { ok: false, error: RoomNoteSaveErrorEnum.NotFound };

            return { ok: true, notes: normalized };
        } catch (error) {
            if (!handleApiError(error)) {
                console.error('Error updating room comment:', error);
            }
            return { ok: false, error: roomNoteSaveErrorFromApiError(error) };
        }
    };

    // user-rights/check answers for the requested right, so the client needs no Shiro matcher
    hasUserRight = async (gbContext: IGbContext, right: string): Promise<boolean> => {
        const projectUrl = this.getProjectUrl(gbContext);
        if (!projectUrl) return false;

        try {
            const response = await axios.get(`${projectUrl}/user-rights/check`, {
                headers: this.getHeaders(),
                params: { 'rights[]': right }
            });

            return coreUserRightsCheckSchema.parse(response.data).data.userRights[right] === true;
        } catch (error) {
            if (!handleApiError(error)) {
                console.error('Error checking user right:', error);
            }
        }
        return false;
    };
}
